import React, { useEffect, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { useApprovals } from './ApprovalsContext.tsx';

const SOURCE_CONFLUENCE_APPROVALS_PARAM = 'source_confluence_approvals';

const i18n = defineMessages({
	approvalUnavailableTitle: {
		id: 'approvals.flag.approval-unavailable.title',
		defaultMessage: 'This approval request is no longer available',
		description:
			'Title for the warning flag shown when an approval request is no longer available (e.g. it was deleted or the user was removed as a reviewer)',
	},
	approvalUnavailableDescription: {
		id: 'approvals.flag.approval-unavailable.description',
		defaultMessage: 'It may have been deleted.',
		description:
			'Description for the warning flag shown when an approval request is no longer available',
	},
});

/**
 * Removes the `source_confluence_approvals` query param from the URL without triggering a page reload.
 * This is a client-side-only operation and must only be called after mount.
 */
const removeSourceConfluenceApprovalsParam = (): void => {
	const urlParams = new URLSearchParams(window.location.search);
	urlParams.delete(SOURCE_CONFLUENCE_APPROVALS_PARAM);
	const newSearch = urlParams.size > 0 ? `?${urlParams.toString()}` : '';
	const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
	window.history.replaceState({}, '', newUrl);
};

const ApprovalUnavailableFlagComponent: FC<WithFlagsProps> = ({ flags }) => {
	const { approvalRequestLoading, isCurrentUserApprover } = useApprovals();

	useEffect(() => {
		// Only run on the client side (this component is never rendered during SSR
		// because it's mounted inside ApprovalsProviderComponent which is guarded
		// by the feature gate check in ApprovalsProvider).
		// We wait until loading is complete before checking so we have accurate data.
		if (approvalRequestLoading) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const isFromApprovalsCta = urlParams.has(SOURCE_CONFLUENCE_APPROVALS_PARAM);

		if (!isFromApprovalsCta) {
			return;
		}

		// Always clean up the query param once we've read it.
		removeSourceConfluenceApprovalsParam();

		// no need to show the warning flag if the current user is still an active reviewer.
		if (isCurrentUserApprover) {
			return;
		}

		// The param is present but the current user is not a reviewer, which means
		// the approval request was cancelled or the user was removed. Show a warning.
		void flags.showWarningFlag({
			title: <FormattedMessage {...i18n.approvalUnavailableTitle} />,
			description: <FormattedMessage {...i18n.approvalUnavailableDescription} />,
			isAutoDismiss: true,
		});
	}, [approvalRequestLoading, isCurrentUserApprover, flags]);

	return null;
};

/**
 * ApprovalUnavailableFlag
 *
 * Detects when a user arrives via an approvals notification CTA (identified by the
 * `source_confluence_approvals` query parameter) but is no longer an active reviewer on the
 * approval request (because the request was cancelled or they were removed).
 *
 * When this condition is met it:
 * 1. Shows a warning flag: "This approval request is no longer available." / "It may have been deleted."
 * 2. Removes the `source_confluence_approvals` query param from the URL to avoid re-showing the flag on
 *    subsequent renders or navigations.
 *
 * This component renders nothing to the DOM (`null`) and is SSR-safe: the flag logic only
 * runs inside a `useEffect` (client-side only), so it will not cause hydration mismatches.
 */
export const ApprovalUnavailableFlag = withFlags(ApprovalUnavailableFlagComponent);
